import React, { useEffect, useState } from "react";
import { IP } from "../../internalServer";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";

function StudentPerformancePDF({
  student,
  subjects,
  assessmentTypeId,
  skillsData,
  studenRankData,
  TotalScore,
  subjectWiseData,
  matchedSubjects,
}) {
  const styles = StyleSheet.create({
    page: {
      padding: 30,
      fontSize: 12,
      fontFamily: "Helvetica",
    },
    headerContainer: {
      flexDirection: "row",
      alignItems: "center",
      width: "100%",
      marginBottom: 10,
    },

    headerLeft: {
      width: "25%",
    },

    headerCenter: {
      width: "50%",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
    },

    headerRight: {
      width: "25%",
      alignItems: "flex-end",
    },

    schoolName: {
      fontSize: 18,
      fontWeight: "bold",
    },

    poweredBy: {
      fontSize: 10,
      color: "#666",
      marginTop: 2,
    },
    dateText: {
      fontSize: 10,
    },
    box: {
      border: "1px solid black",
      padding: 15,
      width: "70%",
      alignSelf: "center",
      display: "flex",
      flexDirection: "column",
      gap: 6,
    },
    field: {
      fontSize: 12,
      lineHeight: 1.5,
    },
    label: {
      fontWeight: "bold",
    },
    overall: {
      border: "1px solid black",
    },
    performanceUpperHalf: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-evenly",
      alignItems: "center",
      gap: 10,
      width: "100%",
      paddingHorizontal: 20,
      paddingVertical: 30,
    },
    performanceLowerHalf: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "center",
      alignItems: "center",
      width: "100%",
    },
    table: {
      width: "auto",
      marginTop: 20,
      borderStyle: "solid",
      borderWidth: 1,
      borderColor: "#000",
    },
    tableRow: {
      flexDirection: "row",
    },
    tableColHeader: {
      width: "25%",
      borderStyle: "solid",
      borderWidth: 1,
      backgroundColor: "#E5E7EB",
      borderColor: "#000",
      padding: 5,
      textAlign: "center",
    },
    tableCol: {
      width: "25%",
      borderStyle: "solid",
      borderWidth: 1,
      borderColor: "#000",
      padding: 5,
    },
    tableCellHeader: {
      fontWeight: "bold",
      fontSize: 10,
    },
    tableCell: {
      fontSize: 10,
      textAlign: "center",
    },
  });
  const [base64Logo, setBase64Logo] = useState(
    "/images/default_school_logo.jpg",
  );

  const today = new Date().toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  const skills = skillsData?.map((skill) => ({
    name: skill.skillDetails[0].name,
    score: Math.round(skill?.percentageMarks),
  }));

  const skillOrder = [
    "Create",
    "Evaluate",
    "Analyze",
    "Apply",
    "Understand",
    "Remember",
    "Unspecified",
  ];

  const sortedSkills = Array.isArray(skills)
    ? skillOrder.map((skillName) => {
        const existingSkill = skills.find((skill) => skill.name === skillName);
        return {
          ...existingSkill,
          name: skillName === "None" ? "Unspecified" : skillName,
          score: existingSkill?.score ?? 0, // Default to 0 if skill is missing
        };
      })
    : [];

  const rankedList = (studenRankData || [])
    // Sort by descending rankPercentage
    .sort((a, b) => b.rankPercentage - a.rankPercentage)
    // Assign realRank based on sorted position
    .map((student, index) => ({
      ...student,
      realRank: index + 1 || "N/A",
    }));

  const studentRank = rankedList.find(
    (rankedStudent) => rankedStudent?._id?.student === student?.id,
  );

  // utils/imageToBase64.ts
  const imageToBase64 = async (imageUrl) => {
    try {
      const response = await fetch(imageUrl);

      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.statusText}`);
      }

      const blob = await response.blob();

      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = (error) => reject(`Error reading file: ${error}`);
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      console.error("Error converting image to base64", err);
      return null;
    }
  };

  useEffect(() => {
    const loadImage = async () => {
      const picPath = student?.school?.profilePic;
      if (!picPath) return; // 🛑 Don't continue if path is missing

      const picUrl = `${IP}${picPath}`;
      const base64 = await imageToBase64(picUrl);
      setBase64Logo(base64);
    };

    loadImage();
  }, [student]);

  const YourScoreData = subjectWiseData?.map((subject) => ({
    maxMarks: subject.totalMaxMarks,
    marksObtained: subject.totalObtainedMarks,
    subjectName: subject.result[0].name,
  }));

  return (
    <Document>
      {/* ================= PAGE 1 ================= */}
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.headerContainer}>
          {/* LEFT EMPTY SPACE */}
          <View style={styles.headerLeft}></View>

          {/* CENTER LOGO + SCHOOL NAME */}
          <View style={styles.headerCenter}>
            <Image
              src={base64Logo}
              style={{
                width: 45,
                height: 45,
                marginRight: 10,
              }}
            />

            <View>
              <Text style={styles.schoolName}>{student?.school?.name}</Text>

              <Text style={styles.poweredBy}>Powered By XploreSense ™</Text>
            </View>
          </View>

          {/* RIGHT DATE */}
          <View style={styles.headerRight}>
            <Text style={{ fontSize: 10 }}>{today}</Text>
          </View>
        </View>
        {/* ===== STUDENT DETAILS ===== */}
        <View
          style={{
            border: "1px solid #000",
            padding: 12,
            marginTop: 10,
            borderRadius: 8,
            alignItems: "center",
          }}
        >
          <Text style={{ fontSize: 14, fontWeight: "bold" }}>
            {student?.name} | Class {student?.standard?.name}
            {student?.section?.name} | Roll No. {student?.code}
          </Text>

          <Text style={{ fontSize: 11, marginTop: 4 }}>
            Admission No: {student?.admissionNumber} | Total Subjects:{" "}
            {subjects?.length}
          </Text>

          <Text style={{ fontSize: 12, fontWeight: "bold", marginTop: 6 }}>
            Marks:{" "}
            {TotalScore[0]?.totalObtainedMarks != null
              ? `${TotalScore[0].totalObtainedMarks.toFixed(0)} / ${
                  TotalScore[0].totalMaxMarks
                }`
              : "NA"}{" "}
            | Result:{" "}
            <Text
              style={{
                color:
                  TotalScore[0]?.result === "Pass"
                    ? "green"
                    : TotalScore[0]?.result === "Fail"
                      ? "red"
                      : "black",
              }}
            >
              {TotalScore[0]?.result ?? "NA"}
            </Text>
          </Text>
        </View>

        {/* ===== OVERALL PERFORMANCE ===== */}
        {/* ===== Overall Performance (UNCHANGED UI) ===== */}
        <View wrap={false} style={[styles.overall, { marginTop: 20 }]}>
          <Text style={{ fontSize: 16, fontWeight: "bold", marginTop: 10 }}>
            OverAll Performance
          </Text>

          <View style={styles.performanceUpperHalf}>
            <Text
              style={{
                fontSize: 16,
                fontWeight: "600",
                padding: 10,
                color: "orange",
              }}
            >
              Rank #{studentRank?.realRank ?? "NA"}
            </Text>

            <View
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                border: "1px solid black",
                padding: 10,
                borderRadius: 10,
                backgroundColor: "#F9FAFB",
              }}
            >
              <Text style={{ fontSize: 12, fontWeight: "bold" }}>
                {student?.name}
              </Text>
              <Text style={{ marginTop: 4 }}>
                Marks –{" "}
                {TotalScore[0]?.totalObtainedMarks != null &&
                TotalScore[0]?.totalMaxMarks != null
                  ? `${TotalScore[0].totalObtainedMarks.toFixed(0)} / ${
                      TotalScore[0].totalMaxMarks
                    }`
                  : "NA"}
              </Text>
            </View>
          </View>

          <View style={styles.performanceLowerHalf}>
            <View style={{ width: "70%", height: 150 }}>
              <Image
                src="/images/coginitive1.png"
                style={{
                  width: "70%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: 10,
                }}
              />
            </View>

            <View
              style={{
                borderWidth: 1,
                borderColor: "#D1D5DB",
                padding: 16,
                borderRadius: 10,
                backgroundColor: "#F9FAFB",
                gap: 10,
                marginRight: 16,
                marginTop: 20,
                marginBottom: 10,
              }}
            >
              {sortedSkills?.map((skill) => (
                <View
                  key={skill.name}
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 10,
                  }}
                >
                  <Text style={{ width: 80, fontWeight: "bold", fontSize: 12 }}>
                    {skill.name}
                  </Text>

                  <View
                    style={{
                      height: 6,
                      flex: 1,
                      backgroundColor: "#E5E7EB",
                      borderRadius: 4,
                      overflow: "hidden",
                    }}
                  >
                    <View
                      style={{
                        height: "100%",
                        width: `${skill.score}%`,
                        backgroundColor: "#4F46E5",
                        borderRadius: 4,
                      }}
                    />
                  </View>

                  <Text
                    wrap={false}
                    style={{
                      width: 45,
                      textAlign: "right",
                      fontSize: 12,
                    }}
                  >
                    {`${skill.score}%`}
                  </Text>
                </View>
              ))}
            </View>
          </View>
        </View>

        {/* ===== FOOTER PAGE 1 ===== */}
        <View
          fixed
          style={{
            position: "absolute",
            bottom: 20,
            left: 30,
            right: 30,
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <Text style={{ fontSize: 10 }}>
            {student?.name} – Performance Report
          </Text>
          <Text style={{ fontSize: 10 }}>Page 1</Text>
        </View>
      </Page>

      {/* ================= PAGE 2 ================= */}
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.headerContainer}>
          {/* LEFT EMPTY SPACE */}
          <View style={styles.headerLeft}></View>

          {/* CENTER LOGO + SCHOOL NAME */}
          <View style={styles.headerCenter}>
            <Image
              src={base64Logo}
              style={{
                width: 45,
                height: 45,
                marginRight: 10,
              }}
            />

            <View>
              <Text style={styles.schoolName}>{student?.school?.name}</Text>

              <Text style={styles.poweredBy}>Powered By XploreSense ™</Text>
            </View>
          </View>

          {/* RIGHT DATE */}
          <View style={styles.headerRight}>
            <Text style={{ fontSize: 10 }}>{today}</Text>
          </View>
        </View>
        {/* table  */}

        <Text style={{ fontSize: 16, fontWeight: "bold", marginTop: 20 }}>
          Subject-wise Performance
        </Text>

        <View style={styles.table}>
          {/* Header Row 1 */}
          <View style={styles.tableRow}>
            <View style={[styles.tableColHeader, { width: "5%" }]}>
              <Text style={styles.tableCellHeader}>S. No.</Text>
            </View>
            <View style={[styles.tableColHeader, { width: "50%" }]}>
              <Text style={styles.tableCellHeader}>Subjects</Text>
            </View>

            {/* Group Headers */}
            <View style={[styles.tableColHeader, { width: "45%" }]}>
              <Text style={styles.tableCellHeader}>Your Score</Text>
            </View>
            {/* <View style={[styles.tableColHeader, { width: '20%' }]}>
              <Text style={styles.tableCellHeader}>Class Score</Text>
            </View>
            <View style={[styles.tableColHeader, { width: '20%' }]}>
              <Text style={styles.tableCellHeader}>Topper Score</Text>
            </View> */}
          </View>

          {/* Header Row 2 */}
          <View style={styles.tableRow}>
            {/* Empty cells for merged headers */}
            <View style={[styles.tableColHeader, { width: "5%" }]} />
            {["Subject Name", "Max Marks"].map((text, i) => (
              <View key={i} style={[styles.tableColHeader, { width: "25%" }]}>
                <Text style={styles.tableCellHeader}>{text}</Text>
              </View>
            ))}

            {/* Your Score Subheaders */}
            {["Marks Obtained"].map((text, i) => (
              <View key={i} style={[styles.tableColHeader, { width: "45%" }]}>
                <Text style={styles.tableCellHeader}>{text}</Text>
              </View>
            ))}

            {/* Class Score Subheaders */}
            {/* {['Marks Obt.', 'Grade'].map((text, i) => (
              <View key={i} style={[styles.tableColHeader, { width: '10%' }]}>
                <Text style={styles.tableCellHeader}>{text}</Text>
              </View>
            ))} */}

            {/* Topper Score Subheaders */}
            {/* {['Marks Obt.', 'Grade'].map((text, i) => (
              <View key={i} style={[styles.tableColHeader, { width: '10%' }]}>
                <Text style={styles.tableCellHeader}>{text}</Text>
              </View>
            ))} */}
          </View>

          {matchedSubjects && matchedSubjects.length > 0 ? (
            matchedSubjects.map((subject, index) => {
              const score = YourScoreData?.find(
                (item) =>
                  item.subjectName?.toLowerCase().trim() ===
                  subject.name?.toLowerCase().trim(),
              );

              return (
                <View key={index} style={styles.tableRow}>
                  <View style={[styles.tableCol, { width: "5%" }]}>
                    <Text style={styles.tableCell}>{index + 1}</Text>
                  </View>
                  <View style={[styles.tableCol, { width: "25%" }]}>
                    <Text style={styles.tableCell}>{subject.name}</Text>
                  </View>
                  <View style={[styles.tableCol, { width: "25%" }]}>
                    <Text style={styles.tableCell}>
                      {score?.maxMarks != null ? score.maxMarks : "N/A"}
                    </Text>
                  </View>
                  <View style={[styles.tableCol, { width: "45%" }]}>
                    <Text style={styles.tableCell}>
                      {score?.marksObtained != null
                        ? score.marksObtained
                        : "N/A"}
                    </Text>
                  </View>
                </View>
              );
            })
          ) : (
            <View style={styles.tableRow}>
              <View style={[styles.tableCol, { width: "100%" }]}>
                <Text style={[styles.tableCell, { textAlign: "center" }]}>
                  No data found
                </Text>
              </View>
            </View>
          )}
        </View>
        {/* ===== FOOTER PAGE 2 ===== */}
        <View
          fixed
          style={{
            position: "absolute",
            bottom: 20,
            left: 30,
            right: 30,
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <Text style={{ fontSize: 10 }}>
            {student?.name} – Performance Report
          </Text>
          <Text style={{ fontSize: 10 }}>Page 2</Text>
        </View>
      </Page>
    </Document>
  );
}

export default StudentPerformancePDF;
